import { useMemo, Dispatch, SetStateAction } from "react"
import { useUrlParamsStateArray } from "./urlParamsState"

/**
 * Creates a type-safe URL state hook with whitelist validation
 *
 * This hook prevents URL parameter injection attacks by validating
 * user-provided values against a whitelist of allowed values.
 *
 * @template T - The validated type (should be a union of string literals)
 * @param key - URL parameter key
 * @param validValues - Array of allowed values
 * @param defaultValue - Safe default value if validation fails
 * @returns Object with validated state and setState function
 *
 * @example
 * ```typescript
 * const VALID_SORTS = ["asc", "desc"] as const
 * type SortOrder = typeof VALID_SORTS[number]
 *
 * const { state: sortOrder, setState: setSortOrder } = useValidatedUrlState(
 *   "sort",
 *   VALID_SORTS,
 *   "asc"
 * )
 * // sortOrder is guaranteed to be "asc" | "desc"
 * ```
 *
 * @example
 * ```typescript
 * const VALID_FILTERS = ["all", "active", "archived"] as const
 * type Filter = typeof VALID_FILTERS[number]
 *
 * const { state: filter, setState: setFilter } = useValidatedUrlState(
 *   "filter",
 *   VALID_FILTERS,
 *   "all"
 * )
 * ```
 */
export function useValidatedUrlState<T extends string>(
  key: string,
  validValues: readonly T[],
  defaultValue: T,
) {
  const { state: rawState, setState } = useUrlParamsStateArray<string[]>(key, [
    defaultValue,
  ])

  // Validate and sanitize the raw URL parameter
  const validatedState = useMemo(() => {
    if (!Array.isArray(rawState) || rawState.length === 0) {
      return defaultValue
    }

    const firstValue = rawState[0]

    if (typeof firstValue !== "string") {
      return defaultValue
    }

    const sanitized = firstValue.toLowerCase().trim()

    // Validate against whitelist
    if (validValues.includes(sanitized as T)) {
      return sanitized as T
    }

    return defaultValue
  }, [rawState, validValues, defaultValue])

  const wrappedSetState: Dispatch<SetStateAction<T>> = (
    action: SetStateAction<T>,
  ) => {
    // Handle both direct values and function updates
    const newValue =
      typeof action === "function"
        ? (action as (prevState: T) => T)(validatedState)
        : action

    setState([newValue])
  }

  return {
    state: validatedState,
    setState: wrappedSetState,
  }
}
